import type { PackageOption } from "./types";

/**
 * Adds minutes to an "HH:mm" time string, returning "HH:mm".
 * Clamps to 23:59 if it exceeds the day.
 */
export function addMinutesToTimeString(timeStr: string, minutes: number): string {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) {
    timeStr = "09:00";
  }
  const [h, m] = timeStr.split(":").map(Number);
  const totalMins = h * 60 + m + minutes;
  const clamped = Math.max(0, Math.min(totalMins, 23 * 60 + 59));
  const newH = Math.floor(clamped / 60);
  const newM = clamped % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
}

/**
 * Subtracts minutes from an "HH:mm" time string, returning "HH:mm".
 * Clamps to 00:00 if it goes below 0.
 */
export function subtractMinutesFromTimeString(timeStr: string, minutes: number): string {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) {
    timeStr = "09:00";
  }
  const [h, m] = timeStr.split(":").map(Number);
  const totalMins = h * 60 + m - minutes;
  const clamped = Math.max(0, totalMins);
  const newH = Math.floor(clamped / 60);
  const newM = clamped % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
}

/**
 * Computes difference in minutes between two "HH:mm" strings.
 * Returns 0 if invalid or end <= start.
 */
export function computeDurationFromTimes(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;
  const diff = endMins - startMins;
  return diff > 0 ? diff : 0;
}

/**
 * Computes start and end times according to SPEC §4.2 & Decision D12:
 * - Default start = end of the person's previous entry that day (or 09:00).
 * - End = start + duration.
 * - Editing either side keeps them consistent.
 */
export function computeStartAndEndTimes(opts: {
  minutes: number;
  previousEntryEndTime?: string | null;
  customStartTime?: string | null;
  customEndTime?: string | null;
}): { startTime: string; endTime: string } {
  const { minutes, previousEntryEndTime, customStartTime, customEndTime } = opts;

  let start: string;
  let end: string;

  if (customStartTime && customEndTime) {
    start = customStartTime;
    end = customEndTime;
  } else if (customStartTime) {
    start = customStartTime;
    end = addMinutesToTimeString(start, minutes);
  } else if (customEndTime) {
    end = customEndTime;
    start = subtractMinutesFromTimeString(end, minutes);
  } else {
    start = previousEntryEndTime && /^\d{2}:\d{2}$/.test(previousEntryEndTime)
      ? previousEntryEndTime
      : "09:00";
    end = addMinutesToTimeString(start, minutes);
  }

  // A slot that would run past midnight ends at 23:59 and starts earlier instead, so the clock times always add up to the hours.
  if (!customEndTime && Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5)) + minutes > 23 * 60 + 59) {
    end = "23:59";
    start = subtractMinutesFromTimeString(end, minutes);
  }

  return { startTime: start, endTime: end };
}

/**
 * Computes the most frequent duration (mode) from a list of durations.
 * Defaults to 60 if empty or no clear winner.
 * SPEC §4.2, D62.
 */
export function computeMostFrequentDuration(durations: number[]): number {
  if (!durations || durations.length === 0) return 60;

  const counts = new Map<number, number>();
  let maxCount = 0;
  let mode = 60;

  for (const d of durations) {
    if (d <= 0) continue;
    const count = (counts.get(d) ?? 0) + 1;
    counts.set(d, count);
    if (count > maxCount) {
      maxCount = count;
      mode = d;
    }
  }

  return mode;
}

/**
 * Inserts or removes a bullet line for a side-panel ticket/escalation into/from the note.
 * SPEC §4.2, D61, T-289:
 * Ticking a task inserts "- <title>" (no task numbers [#id]). Ticking a ticket/escalation inserts "- [<ref>] <title>".
 * Unticking removes either the new format or any legacy "[#id]" line.
 */
export function buildNoteWithItemBullet(
  currentNote: string,
  item: { type: "ticket" | "escalation" | "card" | "checklist"; reference: string; title: string },
  checked: boolean
): string {
  const token = `[${item.reference}]`;
  const isTask = item.type === "card" || item.type === "checklist";
  const titleBullet = `- ${item.title.trim()}`;
  const bullet = isTask ? titleBullet : `- ${token} ${item.title.trim()}`;
  const lines = currentNote ? currentNote.split("\n") : [];

  const lineMatchesItem = (l: string) => {
    // Keep parsing of old "[#id]" lines working
    if (token && l.includes(token)) return true;
    if (l.trim() === titleBullet) return true;
    return false;
  };

  if (checked) {
    if (!lines.some(lineMatchesItem)) lines.push(bullet);
    return lines.join("\n").trim();
  }
  return lines.filter((l) => !lineMatchesItem(l)).join("\n").trim();
}

export interface ProjectOption {
  projectId: number;
  name: string;
  clientId: number | null;
  clientName: string;
  clientLogoUrl: string | null;
  packages: PackageOption[];
  isMine: boolean;
  /** Hours left across the project's packages that have a budget; null when none has one. */
  remainingHours: number | null;
  isOverBudget: boolean;
}

/**
 * The single "Project" list of the time form: one entry per Bexio project, with its packages behind it.
 * Mine first (most used by me first), then by client and name; the query matches project, client and package names.
 */
export function groupPackagesByProject(packages: PackageOption[], query?: string): ProjectOption[] {
  const byProject = new Map<number, PackageOption[]>();
  for (const p of filterAndSortPackages(packages, query)) {
    const list = byProject.get(p.bexioProjectId) ?? [];
    list.push(p);
    byProject.set(p.bexioProjectId, list);
  }
  const out: ProjectOption[] = [];
  for (const [projectId, pkgs] of byProject) {
    const withBudget = pkgs.filter((p) => typeof p.remainingHours === "number");
    out.push({
      projectId,
      name: pkgs[0].bexioProjectName,
      clientId: pkgs[0].clientId,
      clientName: pkgs[0].clientName,
      clientLogoUrl: pkgs[0].clientLogoUrl,
      packages: pkgs,
      isMine: pkgs.some((p) => p.isMine),
      remainingHours: withBudget.length > 0 ? withBudget.reduce((sum, p) => sum + (p.remainingHours as number), 0) : null,
      isOverBudget: pkgs.some((p) => p.isOverBudget),
    });
  }
  const used = (o: ProjectOption) => o.packages.reduce((sum, p) => sum + (p.myUsedHours ?? 0), 0);
  return out.sort((a, b) => Number(b.isMine) - Number(a.isMine) || used(b) - used(a) || a.clientName.localeCompare(b.clientName) || a.name.localeCompare(b.name));
}

/** Moves the item used most often in `usedIds` (latest entries) to the front; the rest keep their order. Ties go to the more recent use. */
export function byMostUsed<T extends { id: number }>(items: T[], usedIds: (number | null)[]): T[] {
  const count = new Map<number, number>();
  for (const id of usedIds) if (id !== null) count.set(id, (count.get(id) ?? 0) + 1);
  let top: number | null = null;
  for (const id of usedIds) if (id !== null && (top === null || count.get(id)! > count.get(top)!)) top = id;
  const first = items.find((i) => i.id === top);
  return first ? [first, ...items.filter((i) => i !== first)] : items;
}

export type ClientOption = { key: string; name: string; logoUrl: string | null; packages: PackageOption[] };

/** Clients of the time form (owner, 2026-11-03: pick the client, then its project): the ones I log on most first, then by name. */
export function groupPackagesByClient(packages: PackageOption[]): ClientOption[] {
  const byClient = new Map<string, ClientOption>();
  for (const p of filterAndSortPackages(packages)) {
    const key = p.clientId !== null ? `c${p.clientId}` : `p${p.bexioProjectId}`;
    const c = byClient.get(key) ?? { key, name: p.clientName || p.bexioProjectName, logoUrl: p.clientLogoUrl, packages: [] };
    c.packages.push(p);
    byClient.set(key, c);
  }
  const used = (c: ClientOption) => c.packages.reduce((sum, p) => sum + (p.myUsedHours ?? 0), 0);
  const mine = (c: ClientOption) => c.packages.some((p) => p.isMine);
  return [...byClient.values()].sort((a, b) => Number(mine(b)) - Number(mine(a)) || used(b) - used(a) || a.name.localeCompare(b.name));
}

/** The package a project resolves to when the person picks it: one of mine first, else the first. */
export function defaultPackageOf(project: ProjectOption): PackageOption {
  return project.packages.find((p) => p.isMine) ?? project.packages[0];
}

/**
 * Filters and sorts packages:
 * "mine first, search all" (SPEC §4.1b, §4.2)
 */
export function filterAndSortPackages(packages: PackageOption[], query?: string): PackageOption[] {
  const q = query?.trim().toLowerCase();

  let list = packages;
  if (q) {
    list = packages.filter((p) => {
      const nameMatch = p.name.toLowerCase().includes(q);
      const clientMatch = p.clientName.toLowerCase().includes(q);
      const projectMatch = p.bexioProjectName.toLowerCase().includes(q);
      return nameMatch || clientMatch || projectMatch;
    });
  }

  // Sort: mine first, then clientName, then name
  return [...list].sort((a, b) => {
    if (a.isMine && !b.isMine) return -1;
    if (!a.isMine && b.isMine) return 1;
    const clientComp = a.clientName.localeCompare(b.clientName);
    if (clientComp !== 0) return clientComp;
    return a.name.localeCompare(b.name);
  });
}

/**
 * One person can't work two things at once: when the slot overlaps entries already on that day, it moves to start after them
 * (same length). `taken` holds [start, end) in minutes since midnight. A slot that would run past midnight is left as it was.
 */
export function shiftPastOverlaps(times: { startTime: string; endTime: string }, taken: [number, number][]): { startTime: string; endTime: string } {
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const span = toMin(times.endTime) - toMin(times.startTime);
  let start = toMin(times.startTime);
  for (const [s, e] of [...taken].sort((a, b) => a[0] - b[0])) {
    if (start < e && start + span > s) start = e;
  }
  if (span <= 0 || start + span > 24 * 60) return times;
  const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return { startTime: hhmm(start), endTime: hhmm(start + span) };
}
